import DesktopWindowsOutlined from "@mui/icons-material/DesktopWindowsOutlined";
import PhoneIphoneOutlined from "@mui/icons-material/PhoneIphoneOutlined";
import { Alert, Box, LinearProgress, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { type FC, type ReactNode, useState } from "react";
import { isEmailChannel } from "../../../modules/channels/types";
import { useMomentPreviewData } from "../../../modules/moment/component/buttons/preview-button/preview-dialog/hooks/use-moment-preview-data";
import { PreviewBody } from "../../../modules/moment/component/buttons/preview-button/preview-dialog/preview-body/preview-body";
import {
  PreviewContextProvider,
  usePreviewContext,
} from "../../../modules/moment/component/buttons/preview-button/preview-dialog/preview-context";
import { PreviewFooter } from "../../../modules/moment/component/buttons/preview-button/preview-dialog/preview-footer";
import { PreviewSidebar } from "../../../modules/moment/component/buttons/preview-button/preview-dialog/preview-sidebar/preview-sidebar";
import type { PreviewMomentData } from "../../../modules/moment/component/buttons/preview-button/preview-dialog/types";
import type { UUID } from "../../../utils/uuid";
import { FOOTER_HEIGHT } from "../../../modules/generic/components/fixed-footer";

type MomentReviewCommonProps = {
  momentId: UUID;
  isNewsletter?: boolean;
  onClose?: () => void;
  buttons?: { sendTest?: ReactNode; publish?: ReactNode };
};

export const MomentReview: FC<
  MomentReviewCommonProps & {
    data?: PreviewMomentData;
  }
> = ({ data, ...props }) => {
  if (data) return <LoadedMomentReview data={data} {...props} />;
  return <MomentReviewLoader {...props} />;
};

const MomentReviewLoader: FC<MomentReviewCommonProps> = (props) => {
  const { data, loading, error } = useMomentPreviewData(props.momentId);

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error.message}</Alert>;
  if (!data) return null;

  return <LoadedMomentReview data={data} {...props} />;
};

const LoadedMomentReview: FC<
  MomentReviewCommonProps & {
    data: PreviewMomentData;
  }
> = ({ data, ...props }) => {
  return (
    <PreviewContextProvider data={data}>
      <MomentReviewInner {...props} />
    </PreviewContextProvider>
  );
};

const MomentReviewInner: FC<MomentReviewCommonProps> = ({
  momentId,
  isNewsletter,
  onClose,
  buttons,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const {
    previewMode,
    setPreviewMode,
    moment_preview_data: { channel },
  } = usePreviewContext();

  // The desktop/mobile switch belongs to the preview, not the page: it sits in the preview header
  // opposite To/From/Subject, so it cannot stray over anything else the page puts beside it.
  const previewModeToggle = isEmailChannel(channel) ? (
    <ToggleButtonGroup
      exclusive
      value={previewMode}
      onChange={(_, value: string | null) => value && setPreviewMode(value as "desktop" | "mobile")}
      sx={{
        backgroundColor: "background.default",
        borderRadius: 999,
        p: 0.5,
        "& .MuiToggleButtonGroup-grouped": {
          border: 0,
          borderRadius: 999,
          height: 32,
          width: 56,
          color: "text.secondary",
          "&.Mui-selected": {
            backgroundColor: "background.paper",
            color: "primary.main",
            boxShadow: "0px 1px 2px 0px rgba(100,116,139,0.2)",
            "&:hover": { backgroundColor: "background.paper" },
          },
        },
      }}
    >
      <ToggleButton
        value="desktop"
        aria-label="Desktop preview"
        data-analytics-id="review-preview-desktop"
      >
        <DesktopWindowsOutlined />
      </ToggleButton>
      <ToggleButton
        value="mobile"
        aria-label="Mobile preview"
        data-analytics-id="review-preview-mobile"
      >
        <PhoneIphoneOutlined />
      </ToggleButton>
    </ToggleButtonGroup>
  ) : undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          // The fixed footer overlays the bottom of the page, so the columns stop above it.
          minHeight: 0,
          pb: FOOTER_HEIGHT,
          overflow: "hidden",
        }}
      >
        <PreviewSidebar open={sidebarOpen} isReview fullHeight />
        <PreviewBody
          momentId={momentId}
          isNewsletter={isNewsletter}
          toggles={previewModeToggle}
          fullHeight
        />
      </Box>
      <PreviewFooter
        sidebarOpen={sidebarOpen}
        toggleSidebar={() => setSidebarOpen((open) => !open)}
        onClose={onClose}
        momentId={momentId}
        isReview
        closePreviewDialog={onClose}
        buttons={buttons}
      />
    </Box>
  );
};
